import { flip, offset, shift } from "@floating-ui/dom";
import { twMerge } from "tailwind-merge";

import { BaseButton, type BaseButtonProps } from "./BaseButton";
import {
  BUTTON_BASE_CLASSES,
  BUTTON_CAUTION_CLASSES,
  BUTTON_DISABLED_WITHOUT_BORDER_CLASSES,
  BUTTON_GHOST_CLASSES,
  BUTTON_SECONDARY_CLASSES,
  BUTTON_SQUIRCLE_CLASSES,
  BUTTON_TERTIARY_CLASSES,
} from "./helpers";

import { Tooltip } from "mds/components/Tooltip";
import { BaseSizeType, LargeSizeType } from "mds/global-types";
import type { PolymorphicRef } from "types";

export const SQUARE_ICON_BUTTON_KINDS = [
  "secondary",
  "tertiary",
  "ghost",
  "caution", // light mode only
  "destructive",
  "destructive-toggle", // dark mode only
] as const;

export type SquareIconButtonKind = (typeof SQUARE_ICON_BUTTON_KINDS)[number];

const COMMON_CLASSES = twMerge(
  BUTTON_BASE_CLASSES,
  BUTTON_DISABLED_WITHOUT_BORDER_CLASSES,
  BUTTON_SQUIRCLE_CLASSES,
  "border",
);

const KIND_CLASSES: Record<SquareIconButtonKind, string> = {
  secondary: BUTTON_SECONDARY_CLASSES,

  tertiary: BUTTON_TERTIARY_CLASSES,

  ghost: BUTTON_GHOST_CLASSES,

  caution: BUTTON_CAUTION_CLASSES,

  destructive:
    "text-white bg-red-shade-20 border-red-shade-20 " +
    "hover:bg-red-shade-40 hover:border-red-shade-40",

  "destructive-toggle":
    "text-white bg-black-tint-20 border-black-tint-20 " +
    "hover:bg-black-tint-30 hover:border-black-tint-30 " +
    "active:bg-red active:border-red " +
    "enabled:data-[selected]:bg-red enabled:data-[selected]:border-red " +
    "enabled:data-[selected]:hover:bg-red-tint-20 enabled:data-[selected]:hover:border-red-tint-20 " +
    // Prevent selected state from overriding aria-disabled styles
    "enabled:aria-disabled:data-[selected]:bg-black-tint-20 enabled:aria-disabled:data-[selected]:border-transparent " +
    "enabled:aria-disabled:data-[selected]:hover:bg-black-tint-20 enabled:aria-disabled:data-[selected]:hover:border-transparent",
};

export type SquareIconButtonSize =
  | Extract<BaseSizeType, "xs" | "s" | "l">
  | Extract<LargeSizeType, "xl">;

const SIZE_CLASSES: Record<SquareIconButtonSize, string> = {
  xs: "size-[28px] min-w-[28px] min-h-[28px] *:size-[12px] rounded-[6px] supports-squircle:rounded-[12px] ",
  s: "size-[34px] min-w-[34px] min-h-[34px] *:size-[16px] rounded-[6px] supports-squircle:rounded-[12px] ",
  l: "size-[46px] min-w-[46px] min-h-[46px] *:size-[18px] rounded-[8px] supports-squircle:rounded-[16px] ",
  xl: "size-[54px] min-w-[54px] min-h-[54px] *:size-[22px] rounded-[10px] supports-squircle:rounded-[20px] ",
};

export type SquareIconButtonProps<T extends React.ElementType> = BaseButtonProps<T> & {
  isSelected?: boolean;
  kind: SquareIconButtonKind;
  size: SquareIconButtonSize;
  // Icon buttons are required to have an aria-label. This label will also be used for the tooltip content.
  "aria-label": string;
};

export const SquareIconButton = function SquareIconButtonInternal({
  ref,
  isSelected,
  kind,
  size,
  className,
  "aria-label": ariaLabel,
  ...rest
}: Omit<SquareIconButtonProps<"button">, "ref"> & { ref?: React.Ref<HTMLButtonElement> }) {
  return (
    <Tooltip
      content={ariaLabel}
      middlewares={[offset(8), flip({ fallbackPlacements: ["bottom"] }), shift({ padding: 8 })]}
      positionStrategy="fixed"
    >
      <BaseButton
        aria-label={ariaLabel}
        className={twMerge(
          COMMON_CLASSES,
          kind ? KIND_CLASSES[kind] : "",
          size ? SIZE_CLASSES[size] : "",
          className,
        )}
        isSelected={isSelected}
        ref={ref}
        {...rest}
      />
    </Tooltip>
  );
} as <T extends React.ElementType = "button">(
  props: SquareIconButtonProps<T> & { ref?: PolymorphicRef<T> },
) => React.ReactNode;
